import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast } from 'react-toastify';
import { parseError } from 'utils/parseTypedErrorMessage';

import { User as LoggedUser } from 'components/MapPage/CorporateViewTabs/ResourceTab/ResourceTabCard';
import { useActualIncidentId } from '../../components/IAP/hooks/commonHooks';

export const workLogKeys = {
  base: ['incidentLogs'] as const,
  byIncident: (incidentId: string) =>
    [...workLogKeys.base, incidentId] as const,
  userLogs: (incidentId: string, userId: string) =>
    [...workLogKeys.base, incidentId, userId] as const,
} as const;

// Types
export interface SignInInformationRow {
  workLogId: string;
  sessionId: string;
  isNew: boolean;
  date: string;
  name: string;
  organization: string;
  icsAssignment: string;
  contactInformation: string;
  initialCheckinComplete?: boolean;
  signInTime: string;
  signOutTime: string;
  userGuid: string;
  workLogVersion?: number;
  sessionVersion?: number;
}

export interface CreateDailyPersonnelEntryFormFields {
  signInTime: Date;
  signOutTime: Date | null;
  userGuid: string;
}

export interface EditDailyPersonnelEntryFormFields {
  signInTime: Date;
  signOutTime: Date | null;
}

export const WORK_LOG_ACTIONS = {
  checkIn: 'check_in',
  checkOut: 'check_out',
  signIn: 'sign_in',
  signOut: 'sign_out',
  activate: 'activate',
  decline: 'decline',
} as const;

export interface Session {
  sessionId: string;
  workLogId: string;
  incidentId: string;
  userGuid: string;
  signInTime: string;
  signOutTime: string | null;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  version: number;
}

export interface WorkLog {
  incidentMemberId: string;
  activatedTimestamp?: Date | null;
  declinedTimestamp?: Date | null;
  activationResendAt?: Date | string | null;
  activationResponse?: string | null;
  editTime: string;
  editedByUsername: string;
  loginTime: string | null;
  logoutTime: string | null;
  workLogId: string;
  initialCheckinComplete: boolean;
  sessions: Session[];
  availableActions: string[];
  activationStatus: string;
  checkInStatus: string;
  signInStatus: string;
}

interface GetLatestStatusParams {
  incidentId: string;
  users: LoggedUser[];
}

export interface IncidentLogEntry {
  emailAddress: string;
  minutesWorkedInWords?: string;
  incidentSeat: string;
  location?: string;
  notes?: string;
  activeTime?: string;
  exitedTime?: string;
  active?: number;
  updatedDatetime?: string;
  userGuid: string;
  nonMemberGuid?: string;
  logs?: WorkLog[];
}

interface CreateSessionParams {
  incidentId: string;
  userGuid: string;
  workLogId: string;
  signInTime: string;
  signOutTime?: string | null;
}

interface UpdateSessionParams {
  incidentId: string;
  userGuid: string;
  workLogId: string;
  sessionId: string;
  signInTime?: string;
  signOutTime?: string | null;
}

interface DeleteSessionParams {
  incidentId: string;
  userGuid: string;
  workLogId: string;
  sessionId: string;
}

export const useGetIncidentLogs = () => {
  const incidentId = useActualIncidentId();
  return useQuery({
    queryKey: workLogKeys.byIncident(incidentId),
    queryFn: async () => {
      const response = await axios<IncidentLogEntry[]>(
        `${config.apiGateway.incident}/${incidentId}/work-logs`,
        {
          method: 'GET',
        }
      );
      return response.data;
    },
    enabled: !!incidentId,
  });
};

export const useGetLatestIncidentWorkLogForUser = (
  incidentId: string,
  userGuid: string,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: workLogKeys.userLogs(incidentId, userGuid),
    queryFn: async () => {
      const response = await axios<WorkLog>(
        `${config.apiGateway.incident}/${incidentId}/users/${userGuid}/work-logs`,
        {
          method: 'GET',
        }
      );
      return response.data;
    },
    enabled: options?.enabled,
  });
};

export const useGetLatestIncidentStatusForUser = () => {
  return useMutation({
    mutationFn: async (data: GetLatestStatusParams) => {
      const responses = await Promise.all(
        data.users.map(async (user) => {
          const response = await axios<string>(
            `${config.apiGateway.incidentMemberStatus(data.incidentId, user.userGuid)}`,
            {
              method: 'GET',
            }
          );
          return {
            status: response.data,
            userName: user.userName || user.emailAddress,
          };
        })
      );

      return responses;
    },
  });
};

// Session Management Functions
export const useCreateIncidentMemberSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateSessionParams) => {
      const response = await axios(
        `${config.apiGateway.incidentMemberWorkLogs(
          data.incidentId,
          data.userGuid
        )}/${data.workLogId}/sessions`,
        {
          method: 'POST',
          data: {
            signInTime: data.signInTime,
            signOutTime: data.signOutTime,
          },
        }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: workLogKeys.byIncident(variables.incidentId),
      });
      toast.success('Session created successfully');
    },
    onError: (error) => {
      toast.error(parseError(error));
    },
  });
};

export const useUpdateIncidentMemberSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateSessionParams) => {
      const updateData: {
        signInTime?: string;
        signOutTime?: string | null;
      } = {};
      if (data.signInTime !== undefined)
        updateData.signInTime = data.signInTime;
      if (data.signOutTime !== undefined)
        updateData.signOutTime = data.signOutTime;

      const response = await axios(
        `${config.apiGateway.incidentMemberWorkLogs(
          data.incidentId,
          data.userGuid
        )}/${data.workLogId}/sessions/${data.sessionId}`,
        {
          method: 'PATCH',
          data: updateData,
        }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: workLogKeys.byIncident(variables.incidentId),
      });
      toast.success('Session updated successfully');
    },
    onError: (error) => {
      toast.error(parseError(error));
    },
  });
};

export const useDeleteIncidentMemberSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: DeleteSessionParams) => {
      const response = await axios(
        `${config.apiGateway.incidentMemberWorkLogs(
          data.incidentId,
          data.userGuid
        )}/${data.workLogId}/sessions/${data.sessionId}`,
        {
          method: 'DELETE',
        }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: workLogKeys.byIncident(variables.incidentId),
      });
      toast.success('Session deleted successfully');
    },
    onError: (error) => {
      toast.error(parseError(error));
    },
  });
};
